export class ApiError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.status = status
  }
}

async function parseError(res: Response) {
  const data = (await res.json().catch(() => null)) as { error?: string } | null
  return data?.error || res.statusText || 'Request failed'
}

export async function api<T>(path: string, init?: RequestInit): Promise<T> {
  const headers = new Headers(init?.headers)
  if (init?.body && !(init.body instanceof FormData) && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json')
  }
  const res = await fetch(path, { credentials: 'include', ...init, headers })
  if (res.status === 401 && !path.endsWith('/admin/login')) {
    if (!window.location.pathname.startsWith('/admin/login')) {
      window.location.assign('/admin/login')
    }
    throw new ApiError('Unauthorized', 401)
  }
  if (!res.ok) throw new ApiError(await parseError(res), res.status)
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

export async function apiUpload(file: File, kind: 'post' | 'author' | 'inline') {
  const body = new FormData()
  body.set('file', file)
  body.set('kind', kind)
  return api<{ key: string; url: string }>('/api/admin/upload', { method: 'POST', body })
}

export type AdminPost = {
  id: number
  slug: string
  title: string
  dek: string
  category: string
  date: string
  image?: string
  imageAlt?: string
  imageCredit?: string
  metaDescription?: string
  author: string
  bodyHtml?: string
  isRecipe: boolean
  ingredients: string[]
  status: 'draft' | 'scheduled' | 'published'
  publishAt?: number
  createdAt: number
  updatedAt: number
}

export type AdminAuthor = {
  id: number
  slug: string
  name: string
  photo?: string
  bio: string
  email?: string
  website?: string
  socials: { type: string; url: string }[]
}

export type AdminStats = {
  totals: { today: number; week: number; month: number; all: number }
  days: { date: string; views: number }[]
  topPages: { path: string; views: number }[]
  devices: { device: string; views: number }[]
  countries: { country: string; views: number }[]
  browsers: { browser: string; views: number }[]
  referrers: { referrer: string; views: number }[]
}

export type AdminUser = {
  id: number | null
  email: string
  name: string
  createdBy: string | null
  createdAt: number | null
  owner: boolean
  you: boolean
  role: 'admin' | 'editor'
}

export type AdminLogEntry = {
  id: number
  level: 'info' | 'warn' | 'error'
  event: string
  message: string
  actor: string | null
  createdAt: number
}

export type AdminLogs = { logs: AdminLogEntry[]; category: string; total: number; page: number; pages: number }

export type NewsletterWindow = { new: number; unsubscribed: number }

export type AdminNewsletter =
  | { configured: false }
  | {
      configured: true
      total: number
      week: NewsletterWindow
      previousWeek: NewsletterWindow
      month: NewsletterWindow
      days: { date: string; views: number }[]
      openRate: number | null
      clickRate: number | null
      campaignCount: number
      recent: { email: string; name: string; joinedAt: string | null; source: string }[]
      campaigns: {
        id: string
        subject: string
        sentAt: string | null
        recipients: number
        openRate: number | null
        clickRate: number | null
      }[]
      mailchimpUrl: string
    }

export type HealthCheck = { key: string; label: string; ok: boolean | null; detail: string }

export type AdminHealth = {
  ok: boolean
  startedAt: number
  uptimeSeconds: number
  version: { commit: string | null; message: string | null; branch: string | null }
  checks: HealthCheck[]
}
